// ---------------------------------------------------------------
// Espace Client - Client API (fetch avec cookies de session)
// Centralise tous les appels vers backend/customer/*
// ---------------------------------------------------------------

export const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Requête JSON avec envoi des cookies de session (credentials: include)
 * nécessaire pour les sessions PHP cross-origin / cross-sous-domaine.
 */
function apiFetch(endpoint, options = {}) {
  const { method = "GET", body = null, params = "" } = options;

  const init = {
    method,
    credentials: "include",
    mode: "cors",
    headers: { "Content-Type": "application/json" },
  };
  if (body) init.body = JSON.stringify(body);

  return fetch(`${API_URL}${endpoint}${params}`, init).then(async (res) => {
    let data = null;
    try {
      data = await res.json();
    } catch (e) {
      data = null;
    }
    if (!res.ok) {
      const message =
        (data && (data.message || data.error)) ||
        `Erreur serveur (${res.status})`;
      throw new ApiError(message, res.status, data);
    }
    return data;
  });
}

export class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.status = status;
    this.data = data;
  }
}

// ---------- Auth ----------

export const checkSession = () =>
  apiFetch("/customer/check_session.php").then((d) => ({
    loggedIn: d.loggedIn,
    customer: d.customer,
  }));

export const login = (email, password) =>
  apiFetch("/customer/login.php", { method: "POST", body: { email, password } });

export const register = (payload) =>
  apiFetch("/customer/register.php", { method: "POST", body: payload });

export const resendVerification = (email) =>
  apiFetch("/customer/resend_verification.php", {
    method: "POST",
    body: { email, site_url: window.location.origin },
  });

export const logout = () =>
  apiFetch("/customer/logout.php", { method: "POST" });

export const verifyEmail = (token) =>
  apiFetch("/customer/verify_email.php", { method: "GET", params: `?token=${encodeURIComponent(token)}` });

// ---------- Dashboard / Réservations ----------

export const getDashboard = (status = "all") =>
  apiFetch("/customer/dashboard.php", { params: `?status=${encodeURIComponent(status)}` });

export const getReservation = (reference) =>
  apiFetch("/customer/reservation_detail.php", { params: `?ref=${encodeURIComponent(reference)}` });

// ---------- Carnet de passagers ----------

export const getPassengers = () =>
  apiFetch("/customer/passengers.php");

export const createPassenger = (payload) =>
  apiFetch("/customer/passengers.php", { method: "POST", body: { ...payload, action: "create" } });

export const updatePassenger = (id, payload) =>
  apiFetch("/customer/passengers.php", { method: "POST", body: { ...payload, action: "update", id } });

export const deletePassenger = (id) =>
  apiFetch("/customer/passengers.php", { method: "POST", body: { action: "delete", id } });

// ---------- Profil ----------

export const updateProfile = (payload) =>
  apiFetch("/customer/profile.php", { method: "POST", body: { ...payload, action: "profile" } });

export const updatePassword = (currentPassword, newPassword) =>
  apiFetch("/customer/profile.php", {
    method: "POST",
    body: { action: "password", currentPassword, newPassword },
  });

// ---------- Mot de passe oublié ----------

export const forgotPassword = (email) =>
  apiFetch("/customer/forgot_password.php", {
    method: "POST",
    body: { email, site_url: window.location.origin },
  });

export const resetPassword = (token, newPassword) =>
  apiFetch("/customer/reset_password.php", {
    method: "POST",
    body: { token, new_password: newPassword },
  });

// ---------- Utilitaires d'affichage ----------

export const STORAGE_KEY = "djater_customer_guest";

export const TYPE_LABELS = {
  ADULT: "Adulte",
  CHILD: "Enfant",
  HELD_INFANT: "Bébé",
};

export const formatAmount = (amount, currency = "XOF") => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  return `${new Intl.NumberFormat("fr-FR").format(Math.round(n))} ${currency}`;
};

export const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
};

export const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" });
};